import { useDispatch, useSelector } from 'react-redux';
import {
  fetchClientByClientIdRequest,
  fetchTypeformPayloadByClientIdRequest,
  selectClientDataExists,
  selectClientIsLoading,
  selectMaybeClient,
  selectTypeformPayload,
  selectTypeformPayloadIsLoading,
  SheetModal,
} from '..';
import { useEffect, useMemo, useState } from 'react';
import {
  possessive,
  fullName,
  ClientStatus,
  QUICK_EDIT_ALL_FIELDS,
  usesEdjQuickPlanModelInput,
} from '@shared';
import { useNavigateToIntakeForm } from '@client/hooks/useNavigateToIntakeForm';
import { ALGORITHMS_ERROR } from '@client/util/constants';
import { useRecalculateQuickEstimate } from '@/features/client-quick-plan/hooks/use-recalculate-quick-estimate';
import { findClientIntakeSurvey } from '@/shared/lib/utils';
import { useQuickEditIntake } from '@client/hooks/useQuickEditIntake';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { Button } from './ui/button';
import { Loader2, Pencil } from 'lucide-react';
import { QuickEditForm, QUICK_EDIT_FORM_ID } from './QuickEditForm';
import { PilotRestricted } from '@/shared/components/pilot-restricted';

import clientIntakeFormSchema from '@shared/utils/clientIntakeFormSchema.json';
import { isArray } from 'lodash';

const PREFERENCE_DISPLAY_LABELS: Record<string, Record<string, string>> = {
  clientIndependencePreference: {
    home: 'Home-focused',
    balances: 'Balanced',
    comprehensive: 'Comprehensive care',
  },
  clientFinancialSecurityPreference: {
    affordable: 'Protect assets',
    balance: 'Balanced',
    prioritize: 'Best care',
  },
  clientFamilyInvolvementPreference: {
    'no direct care': 'None',
    minimal: 'Minimal',
    balance: 'Balanced',
    heavily: 'Heavily involved',
  },
};

export function ClientDataModal({
  partialClient,
  open,
  initialMode = 'view',
  quickEstimateOnly = false,
  onClose,
}: {
  partialClient: Client;
  open: boolean;
  initialMode?: 'view' | 'edit';
  /**
   * Opened from "Edit Quick Estimate" (EDJ Quick Plan clients): always the EDJ edit form, no link
   * to the full questionnaire (EDJ restricts it), and Cancel or Save closes the modal instead of
   * falling back to the intake-data view. When the estimate failed ("Algorithms error"), saving
   * with no changes retries it from the saved answers.
   */
  quickEstimateOnly?: boolean;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const clientIsLoading = useSelector(selectClientIsLoading);
  const clientDataExists = useSelector(selectClientDataExists);
  const typeformPayload = useSelector(selectTypeformPayload);
  const typeformPayloadIsLoading = useSelector(selectTypeformPayloadIsLoading);
  const { navigateToIntakeForm, isLoading: isLoadingForm } = useNavigateToIntakeForm({
    clientId: partialClient.clientId,
  });
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const isClientActive = partialClient.clientStatus === ClientStatus.active;

  const hasIntakeSurvey = (!!partialClient.intakeSurvey || isClientActive) && !!partialClient.formReferenceId;
  const { mode, setMode, save, isPending: isQuickEditPending, isRefreshing, clearRefreshing } = useQuickEditIntake({
    clientId: partialClient.clientId,
    onSaveSuccess: quickEstimateOnly ? onClose : undefined,
    successMessage: quickEstimateOnly ? 'Quick estimate updated. Recalculating results.' : undefined,
  });

  const retryOnNoChanges =
    quickEstimateOnly && !!partialClient.clientTags?.includes(ALGORITHMS_ERROR);
  const { recalculateQuickEstimate, isRecalculating } = useRecalculateQuickEstimate({ onSuccess: onClose });
  const isSaving = isQuickEditPending || isRecalculating;

  const clientName = fullName(partialClient);
  const [isQuickEditValid, setIsQuickEditValid] = useState(true);

  const clientDataReady =
    clientDataExists &&
    !clientIsLoading &&
    !typeformPayloadIsLoading &&
    (!typeformPayload || typeformPayload.clientId === partialClient.clientId);

  useEffect(() => {
    if (isRefreshing && clientDataReady) {
      clearRefreshing();
    }
  }, [isRefreshing, clientDataReady, clearRefreshing]);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      dispatch(fetchClientByClientIdRequest(partialClient.clientId));
      dispatch(fetchTypeformPayloadByClientIdRequest({ clientId: partialClient.clientId }));
    }
  }, [open, partialClient.clientId, dispatch, initialMode, setMode]);

  const handleClose = () => {
    setMode('view');
    onClose();
  };

  const title = quickEstimateOnly
    ? 'Edit Quick Estimate'
    : mode === 'edit'
      ? 'Quick Edit Mode'
      : clientName ?? 'Client Information';

  const subTitle = retryOnNoChanges
    ? 'This estimate could not be calculated. Correct the details, or save without changes to try again.'
    : quickEstimateOnly
    ? 'Update the details this quick estimate is built from. Saving recalculates the estimate.'
    : mode === 'edit'
      ? 'Edit commonly updated fields below. Only a subset of intake fields can be modified here.'
      : `Information provided in ${clientName ? `${possessive(clientName)} ` : 'the '} intake form.`;

  const cancelEdit = quickEstimateOnly ? handleClose : () => setMode('view');

  const primaryButton =
    mode === 'edit' ? (
      <div className="flex w-full flex-col">
        {!quickEstimateOnly && (
          <div className="border-b pb-3 text-center">
            <button
              type="button"
              onClick={async () => { await navigateToIntakeForm(); handleClose(); }}
              disabled={isLoadingForm}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-hover underline underline-offset-2 disabled:opacity-50"
            >
              {isLoadingForm ? 'Loading...' : (
                <>
                  Need to make additional changes? Access your full intake questionnaire
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                </>
              )}
            </button>
          </div>
        )}
        <div className={quickEstimateOnly ? 'flex gap-2' : 'flex gap-2 pt-4'}>
          <Button
            type="button"
            variant="outline"
            onClick={cancelEdit}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={QUICK_EDIT_FORM_ID}
            disabled={isSaving || !isQuickEditValid}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    ) : hasIntakeSurvey ? (
      <PilotRestricted capability="intake-form">
        <Button
          type="button"
          onClick={() => setMode('edit')}
        >
          <Pencil className="mr-2 h-4 w-4" />
          {`Edit ${clientName ? `${possessive(clientName)}` : 'Client'} Data`}
        </Button>
      </PilotRestricted>
    ) : undefined;

  return (
    <SheetModal
      open={open}
      onClose={handleClose}
      title={title}
      subTitle={subTitle}
      className="min-w-[95%] sm:min-w-[90%] md:min-w-[700px] lg:min-w-[900px] xl:min-w-[1000px]"
      primaryButton={primaryButton}
    >
      <ClientDataModalContent
        mode={mode}
        clientDataReady={clientDataReady && !isRefreshing}
        quickEstimateOnly={quickEstimateOnly}
        onCancelEdit={cancelEdit}
        onSave={save}
        onNoChanges={retryOnNoChanges ? () => recalculateQuickEstimate(partialClient.clientId) : undefined}
        onValidChange={setIsQuickEditValid}
      />
    </SheetModal>
  );
}

function ClientDataModalContent({
  mode,
  clientDataReady,
  quickEstimateOnly,
  onCancelEdit,
  onSave,
  onNoChanges,
  onValidChange,
}: {
  mode: 'view' | 'edit';
  clientDataReady: boolean;
  quickEstimateOnly: boolean;
  onCancelEdit: () => void;
  onSave: (fields: Record<string, string | number | null>) => void;
  onNoChanges?: () => void;
  onValidChange: (valid: boolean) => void;
}) {
  const client = useSelector(selectMaybeClient);

  if (!clientDataReady || !client) {
    return <ClientDataDescriptionList isLoading={true} />;
  }

  // A quick estimate whose model run failed has no calculated client data yet; fixing its answers
  // is exactly what this mode is for, so read the survey as the server sent it.
  const intakeSurvey = quickEstimateOnly ? findClientIntakeSurvey(client) : client.intakeSurvey;

  if (mode === 'edit' && intakeSurvey) {
    return (
      <QuickEditForm
        key={intakeSurvey.surveyVersionId}
        intakeSurvey={intakeSurvey}
        edjQuickPlan={quickEstimateOnly || usesEdjQuickPlanModelInput(client)}
        onCancel={onCancelEdit}
        onSave={onSave}
        onNoChanges={onNoChanges}
        onValidChange={onValidChange}
      />
    );
  }

  return <ClientDataDescriptionList isLoading={false} />;
}

interface DetailRowProps {
  label: string;
  value: string | React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
    <dt className="text-sm font-medium leading-6 text-gray-900">{label}</dt>
    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{value}</dd>
  </div>
);

function ClientDataDescriptionList({ isLoading }: { isLoading: boolean }) {
  const client = useSelector(selectMaybeClient);
  const typeformPayload = useSelector(selectTypeformPayload);

  // Parse and merge typeform data with intakeSurvey
  const mergedSurveyData = useMemo(() => {
    if (!client?.intakeSurvey) {
      return null;
    }

    const merged = { ...client.intakeSurvey } as Record<string, any>;

    // Only merge if typeformPayload belongs to this client
    if (typeformPayload?.typeformPayload && typeformPayload.clientId === client.clientId) {
      try {
        const parsed = JSON.parse(typeformPayload.typeformPayload);

        Object.keys(parsed).forEach(key => {
          // Extract field name (before ::)
          const fieldName = key.split('::')[0];
          const schemaEntry = (clientIntakeFormSchema as Record<string, any>)[fieldName];
          if (!schemaEntry) return;

          // For list-type fields, prefer DB value when it's a non-empty array (includes "other" values)
          const existingValue = client.intakeSurvey[fieldName as keyof typeof client.intakeSurvey];
          if (
            schemaEntry.type === 'list' &&
            isArray(existingValue) &&
            existingValue.length > 0
          ) {
            merged[fieldName] = existingValue;
          } else if (
            fieldName !== 'clientHeightInches' &&
            (QUICK_EDIT_ALL_FIELDS as readonly string[]).includes(fieldName) &&
            existingValue != null
          ) {
            // For quick-edit fields, prefer DB values (they may have been updated via quick edit)
            merged[fieldName] = existingValue;
          } else {
            merged[fieldName] = parsed[key];
          }
        });
      } catch (error) {
        console.error('Error parsing typeformPayload:', error);
      }
    }

    return merged;
  }, [client?.intakeSurvey, client?.clientId, typeformPayload]);

  if (isLoading || !client) {
    return (
      <div className="my-10 flex h-64 items-center justify-center">
        <LoadingSkeleton />
      </div>
    );
  }

  const { intakeSurvey, introIntakeSurvey } = client;

  const formatValue = (value: any, type: string, fieldName: string) => {
    switch (type) {
      case 'string': {
        if (!value) return 'N/A';
        const fieldLabels = PREFERENCE_DISPLAY_LABELS[fieldName];
        if (fieldLabels) {
          const key = typeof value === 'string' ? value.toLowerCase() : value;
          return fieldLabels[key] ?? value;
        }
        return value;
      }
      case 'integer':
        return value ? value.toString() : 'N/A';
      case 'number':
        return value ? `$${parseFloat(value).toLocaleString()}` : 'N/A';
      case 'percent':
        return value ? `${parseFloat(value)}%` : 'N/A';
      case 'list':
        if (!value) return 'None';
        // If it's an array, use as is; if it's a string, split by delimiter (|||)
        const arr = isArray(value)
          ? value
          : typeof value === 'string'
            ? value.split('|||').map(item => item.trim())
            : [value];
        if (arr.length < 1) return 'None';
        return (
          <ul className="list-inside list-disc">
            {arr.map((item: any, index: number) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        );
      case 'date':
        if (!value) return 'N/A';
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        });
      default:
        return 'N/A';
    }
  };

  const renderSurveyDetails = <T extends Record<string, any>>(surveyData: T) => {
    const sortedDetails = Object.entries(clientIntakeFormSchema)
      .filter(([key]) => key in surveyData) // Ensures we only include fields present in surveyData
      .sort((a, b) => a[1].group.localeCompare(b[1].group));

    return sortedDetails.map(([key, { label, type }]) => (
      <DetailRow
        key={key}
        label={label}
        value={formatValue(surveyData[key as keyof T], type, key)}
      />
    ));
  };

  return (
    <div
      data-hj-suppress
      className="mt-6 "
      data-private
    >
      {introIntakeSurvey && (
        <>
          <h2 className="text-base font-semibold text-gray-700">Client Information</h2>
          <dl className="divide-y divide-gray-100">
            {renderSurveyDetails<IntroIntakeSurvey>(introIntakeSurvey)}
          </dl>
        </>
      )}
      <h2 className="mt-6 text-base font-semibold text-gray-700">Intake Survey Answers</h2>
      <dl
        data-testid="survey-details-renderer"
        className="divide-y divide-gray-100"
      >
        {(mergedSurveyData || intakeSurvey) ? renderSurveyDetails<IntakeSurvey>((mergedSurveyData || intakeSurvey) as IntakeSurvey) : (
          <p className="py-4 text-sm text-gray-500">No intake survey data available.</p>
        )}
      </dl>
    </div>
  );
}

const LoadingSkeleton: React.FC = () => {
  return (
    <div
      role="status"
      className="w-full animate-pulse space-y-4 divide-y divide-gray-200 rounded  p-4  md:p-6 "
    >
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="flex items-center justify-between pt-4 first:pt-0"
        >
          <div>
            <div className="mb-2.5 h-2.5 w-24 rounded-full bg-gray-300 "></div>
            <div className="h-2 w-32 rounded-full bg-gray-200 "></div>
          </div>
          <div className="h-2.5 w-12 rounded-full bg-gray-300 "></div>
        </div>
      ))}
      <span className="sr-only">Loading...</span>
    </div>
  );
};
